import { Link, createFileRoute } from '@tanstack/react-router'
import { Loader2 } from 'lucide-react'
import { TermsTypeSection } from './-components/TermsTypeSection'
import { PageTitle } from '@/components/PageTitle'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useTermsGroups } from '@/hooks/repositories/useTermsRepository'
import { requirePermission } from '@/lib/permissions/route-guards'
import { BACKOFFICE_PERMISSION_KEYS } from '@/lib/permissions/backoffice-permissions'

export const Route = createFileRoute('/_app/backoffice/terms/')({
  component: RouteComponent,
  beforeLoad: requirePermission(BACKOFFICE_PERMISSION_KEYS.terms),
})

function RouteComponent() {
  const { data: groups, isLoading, isError, refetch } = useTermsGroups()

  return (
    <div className="py-4">
      <PageTitle title="Terms" />

      <Breadcrumb className="mb-4">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/backoffice">Backoffice</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Terms</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="mb-4">
        <h1 className="text-3xl font-bold tracking-tight">Terms</h1>
        <p className="text-muted-foreground mt-2">
          Legal terms clients accept in the portal. Every change publishes a new
          version; clients see the active one.
        </p>
      </div>

      {isLoading ? (
        <Card className="flex items-center justify-center p-8">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </Card>
      ) : isError ? (
        <Card className="flex flex-col items-center justify-center gap-2 p-8 text-center">
          <p className="text-sm text-destructive">Failed to load terms.</p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => void refetch()}
          >
            Retry
          </Button>
        </Card>
      ) : !groups || groups.length === 0 ? (
        <Card className="flex items-center justify-center p-8 text-center">
          <p className="text-sm text-muted-foreground">
            No terms types configured yet.
          </p>
        </Card>
      ) : (
        <div className="space-y-6">
          {groups.map((group) => (
            <TermsTypeSection key={group.type} group={group} />
          ))}
        </div>
      )}
    </div>
  )
}
